import React from 'react';
import { Volume2, Play, Pause } from 'lucide-react';

interface AudioControlsProps {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  onPlay: (start: number, end: number) => void;
  onPause: () => void;
  audioSegment?: {
    text: string;
    start: number;
    end: number;
    audio_url?: string;
  } | null;
}

export function AudioControls({
  isPlaying,
  currentTime,
  duration,
  onPlay,
  onPause,
  audioSegment
}: AudioControlsProps) {
  const isMobile = typeof window !== 'undefined' && 
    ('ontouchstart' in window || navigator.maxTouchPoints > 0);
    
  const handlePlayPause = () => {
    if (isPlaying) {
      onPause();
    } else if (audioSegment) {
      if (isMobile) {
        onPause(); // First ensure any current playback is stopped
        setTimeout(() => {
          onPlay(audioSegment.start, audioSegment.end);
        }, 200);
      } else {
        onPlay(audioSegment.start, audioSegment.end);
      }
    }
  };

  const handleSeek = (time: number) => {
    if (!audioSegment || !isFinite(time)) return;
    
    // Always pause first to ensure clean state
    onPause();
    
    // Add slight delay to ensure cleanup is complete
    setTimeout(() => {
      if (audioSegment) { // Double-check audioSegment still exists after timeout
        // If within the current segment, seek to that position
        if (time >= audioSegment.start && time <= audioSegment.end) {
          onPlay(time, audioSegment.end);
        } else {
          // Otherwise, play from the start of the segment
          onPlay(audioSegment.start, audioSegment.end);
        }
      }
    }, isMobile ? 250 : 100); // Longer delay for mobile
  };

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs < 10 ? '0' : ''}${secs}`;
  };

  return (
    <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
      <button
        onClick={handlePlayPause}
        className="flex items-center justify-center w-10 h-10 text-indigo-600 hover:text-indigo-800 border border-indigo-600 rounded-full hover:bg-indigo-50 transition-colors"
      >
        {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
      </button>
      
      <div className="flex-1 flex items-center gap-2">
        <span className="text-sm text-gray-600 w-12 text-right">
          {formatTime(currentTime)}
        </span>
        <input
          type="range"
          min="0"
          max={duration || 100}
          value={currentTime}
          onChange={(e) => handleSeek(Number(e.target.value))}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
        />
        <span className="text-sm text-gray-600 w-12">
          {formatTime(duration)}
        </span>
      </div>
      
      {audioSegment && (
        <div className="text-sm text-gray-600 hidden sm:block">
          {audioSegment.text.length > 30 
            ? audioSegment.text.substring(0, 30) + '...' 
            : audioSegment.text}
        </div>
      )}
    </div>
  );
}